export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  categorySlug: string;
  author: string;
  date: string;
  readTime: string;
  imageUrl: string;
  tags: string[];
}

export interface Category {
  name: string;
  slug: string;
  description: string;
  icon: string;
}

export const categories: Category[] = [
  {
    name: '合同起草',
    slug: 'contract-drafting',
    description: '学习如何高效协作起草各类合同文件，确保条款准确、权责清晰',
    icon: 'ri-file-text-line',
  },
  {
    name: '方案策划',
    slug: 'proposal-planning',
    description: '团队协同策划营销方案、商业计划书与项目提案',
    icon: 'ri-lightbulb-line',
  },
  {
    name: '创意写作',
    slug: 'creative-writing',
    description: '多人实时头脑风暴与创意内容联合撰写',
    icon: 'ri-edit-line',
  },
  {
    name: '技术文档',
    slug: 'technical-docs',
    description: '团队协同编写技术规范、API文档与产品说明书',
    icon: 'ri-code-line',
  },
  {
    name: '会议纪要',
    slug: 'meeting-minutes',
    description: '实时记录与整理会议内容，多人补充完善要点',
    icon: 'ri-chat-3-line',
  },
  {
    name: '学术论文',
    slug: 'academic-papers',
    description: '研究团队共同撰写与修改学术论文',
    icon: 'ri-book-open-line',
  },
];

export const articles: Article[] = [
  {
    id: '1',
    title: '多人协同起草合同：5个提升效率的实用技巧',
    slug: 'contract-collaboration-tips',
    excerpt: '在团队协作起草合同时，如何避免版本混乱、确保条款一致性？本文分享5个经过实战检验的高效技巧，帮助你的团队告别反复传文件的烦恼。',
    content: `
      <h2>引言</h2>
      <p>在当今快节奏的商业环境中，合同起草已成为团队协作中的一项核心任务。然而，传统的合同起草方式——通过邮件反复发送Word文档、手动合并修改意见——不仅效率低下，还容易出错。一起草平台正是为解决这一痛点而生。</p>

      <h2>技巧一：建立清晰的版本管理机制</h2>
      <p>在多人协同起草合同时，最令人头疼的就是版本混乱。谁改了什么？哪个版本是最新的？一起草的实时协同编辑功能让所有参与者同时在一个文档上工作，所有修改实时同步，彻底告别版本管理的困扰。系统自动保留修改历史，你可以随时回溯到任意历史版本，不用担心误操作导致的内容丢失。</p>

      <h2>技巧二：使用模板加速起草流程</h2>
      <p>每次从零开始起草合同不仅耗时，还容易遗漏关键条款。一起草提供了丰富的合同模板库，涵盖采购合同、服务协议、保密协议、劳动合同等常见类型。团队可以选择合适的模板作为起点，在此基础上进行定制化修改，大幅缩短起草时间。</p>

      <h2>技巧三：明确协作角色与权限</h2>
      <p>一份合同的起草往往涉及多个角色：起草人、审核人、法务、管理层等。一起草支持精细化权限控制，你可以设置不同成员的编辑权限、批注权限和只读权限。法务人员可以添加批注但无法修改正文，管理员可以在最终版本定稿后锁定文档，确保内容的权威性。</p>

      <h2>技巧四：利用批注与讨论功能高效沟通</h2>
      <p>传统合同起草中，讨论往往发生在邮件或即时通讯工具中，与文档内容脱离，上下文缺失。一起草内置的批注和讨论功能让沟通直接发生在文档的对应位置。团队成员可以针对特定条款发起讨论，所有讨论记录都保存在文档中，方便追溯查阅。</p>

      <h2>技巧五：制定规范的审阅流程</h2>
      <p>合同起草不是一蹴而就的，通常需要多轮审阅。一起草支持自定义工作流程，你可以设定起草→内部审核→法务审核→最终批准的标准流程。每个环节的审阅意见都被完整记录，合同定稿后自动生成审阅日志，满足企业合规要求。</p>

      <h2>总结</h2>
      <p>多人协同起草合同不再是一件让人头疼的事。借助一起草的实时协作、版本管理、模板库和精细化权限等功能，团队可以高效、准确地完成合同起草工作。立即注册一起草，开启高效协作起草之旅。</p>
    `,
    category: '合同起草',
    categorySlug: 'contract-drafting',
    author: '张明华',
    date: '2026-08-08',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20professional%20office%20with%20multiple%20people%20collaborating%20on%20legal%20documents%2C%20warm%20amber%20lighting%2C%20clean%20minimal%20workspace%2C%20professional%20atmosphere%2C%20soft%20shadows%2C%20editorial%20photography%20style%20with%20organic%20composition&width=800&height=450&seq=article-contract-01&orientation=landscape',
    tags: ['合同协作', '效率提升', '团队协作', '版本管理'],
  },
  {
    id: '2',
    title: '营销方案协同编写：让跨部门协作不再难',
    slug: 'marketing-proposal-collaboration',
    excerpt: '跨部门协作编写营销方案时，信息不对称和沟通延迟是最大障碍。一起草如何帮助市场、产品、设计团队无缝协同？看完这篇就懂了。',
    content: `
      <h2>引言</h2>
      <p>一份成功的营销方案，往往需要市场部、产品部、设计部甚至财务部的通力合作。传统方式下，各部门各自为政，方案在反复传递中容易丢失关键信息。一起草提供的实时协同编辑平台，让跨部门协作变得前所未有的顺畅。</p>

      <h2>打破部门壁垒的协作模式</h2>
      <p>一起草的核心优势在于"同时同文档"的协作模式。市场部起草方案框架后，产品部可以实时补充产品卖点和数据，设计部同步添加视觉方案建议，所有修改在一个文档中实时呈现，不必等待邮件往返，不必手动合并版本。这种协作模式将方案编写周期从数周缩短到数天。</p>

      <h2>实时批注让反馈更精准</h2>
      <p>在方案编写过程中，精准的反馈至关重要。一起草的实时批注功能允许团队成员在文档任意位置添加评论和建议。你可以@某位同事请求其确认某个数据，也可以对某个段落提出修改建议。所有批注按时间线排列，讨论脉络清晰可循。</p>

      <h2>多端同步，随时随地的协作</h2>
      <p>团队成员可能在不同的设备上工作——有人在办公室用电脑，有人在外出时用平板，有人用手机查看进度。一起草支持Web、iOS和Android多端同步，所有修改实时更新到云端，确保每位成员看到的始终是最新版本。</p>

      <h2>总结</h2>
      <p>营销方案编写不再是跨部门协作的"噩梦"。一起草用实时协同编辑、精准批注和多端同步三大能力，帮助团队将精力聚焦在方案质量本身，而不是沟通成本上。试试一起草，体验全新的协作方式。</p>
    `,
    category: '方案策划',
    categorySlug: 'proposal-planning',
    author: '李婷',
    date: '2026-08-05',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Diverse%20team%20brainstorming%20marketing%20strategy%20in%20modern%20office%2C%20warm%20natural%20light%2C%20sticky%20notes%20on%20whiteboard%2C%20collaborative%20atmosphere%2C%20clean%20composition%2C%20editorial%20style%20photography&width=800&height=450&seq=article-marketing-02&orientation=landscape',
    tags: ['营销方案', '跨部门协作', '方案策划', '工作效率'],
  },
  {
    id: '3',
    title: '创意写作团队的协作利器：从脑暴到终稿的全流程',
    slug: 'creative-writing-workflow',
    excerpt: '创意写作需要灵感的碰撞和反复打磨。一起草为创意团队提供了从头脑风暴到最终定稿的完整协作工作流，让好创意不再半途而废。',
    content: `
      <h2>引言</h2>
      <p>创意写作——无论是广告文案、品牌故事还是内容营销稿件——都离不开团队的集体智慧。但创意的产生和管理往往是最难规范化的。一起草为创意团队打造了一套完整的协作工作流，覆盖从灵感收集到最终交付的全过程。</p>

      <h2>阶段一：灵感收集与头脑风暴</h2>
      <p>一起草支持创建"灵感板"文档，团队成员可以随时添加想法、链接、图片和参考案例。不同于传统的共享文件夹，灵感板中的每个条目都支持评论和投票，团队可以快速筛选出最有潜力的创意方向。这种开放式的协作模式极大地激发了团队创造力。</p>

      <h2>阶段二：大纲共创与结构优化</h2>
      <p>确定创意方向后，团队进入大纲共创阶段。每位成员可以在大纲中贡献自己的思路，编辑者可以实时看到其他人的补充和调整。一起草的大纲视图让文章结构一目了然，拖拽排序功能让结构调整变得轻松直观。</p>

      <h2>阶段三：分章节协同撰写</h2>
      <p>对于长篇文章或系列内容，团队可以按章节分工撰写。一起草支持分章节独立编辑，同时保持整篇文档的一致性。每位写作者可以专注于自己负责的部分，同时随时查看他人的进度和内容，确保风格统一、逻辑连贯。</p>

      <h2>阶段四：集中审校与定稿</h2>
      <p>全部内容完成后，进入集中审校阶段。主编或项目负责人可以使用一起草的"审阅模式"，逐段审阅并提供修改意见。修改意见以批注形式呈现，作者可以直接在批注中回应并确认修改。定稿后一键导出为PDF或Word格式。</p>

      <h2>总结</h2>
      <p>从灵感迸发到精品出炉，一起草陪伴创意团队走过每一步。实时协作让创意不再被流程所困，让好想法更快落地。</p>
    `,
    category: '创意写作',
    categorySlug: 'creative-writing',
    author: '王思远',
    date: '2026-08-01',
    readTime: '7 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Creative%20team%20collaborating%20in%20modern%20loft%20office%20with%20large%20whiteboard%2C%20warm%20afternoon%20light%2C%20plants%20and%20art%20on%20walls%2C%20creative%20brainstorming%20atmosphere%2C%20editorial%20photography&width=800&height=450&seq=article-creative-03&orientation=landscape',
    tags: ['创意写作', '脑暴', '内容创作', '协作流程'],
  },
  {
    id: '4',
    title: '技术文档协同编写最佳实践：从混乱到有序',
    slug: 'technical-docs-best-practices',
    excerpt: '技术文档编写涉及产品经理、开发工程师、技术写作人员等多方协作。如何用一起草让技术文档编写从混乱走向有序？',
    content: `
      <h2>引言</h2>
      <p>技术文档是产品的重要组成部分，但其编写过程往往充满挑战：产品经理关注用户场景，工程师关注技术细节，技术写作人员关注表达规范。多方视角的融合需要高效的协作工具。一起草为技术文档编写团队提供了理想的协同平台。</p>

      <h2>结构化模板让文档规范统一</h2>
      <p>技术文档有严格的规范要求——从标题层级到代码示例格式，从术语表到版本标注。一起草支持团队创建自定义模板，预设文档结构和格式规范。每位成员基于统一模板开始编写，从源头保证文档风格的一致性。模板支持Markdown语法，技术人员上手零门槛。</p>

      <h2>多人实时编辑与版本对比</h2>
      <p>产品经理描述用户场景，工程师补充技术实现细节，技术写作人员优化表达——所有人在同一份文档中工作，实时同步。一起草的版本对比功能可以清晰展示任意两个版本之间的差异，方便技术审核时快速定位修改内容。</p>

      <h2>融入开发工作流</h2>
      <p>一起草支持与主流项目管理工具的集成，可以直接将文档编写任务关联到具体的开发迭代。当产品功能更新时，相关的文档编写任务自动触发通知，确保文档与产品同步更新，不再出现"文档滞后"的尴尬。</p>

      <h2>总结</h2>
      <p>技术文档编写不再是开发流程中的"拖后腿"环节。一起草的结构化模板、实时协作和版本管理能力，帮助技术团队高效产出高质量的文档。</p>
    `,
    category: '技术文档',
    categorySlug: 'technical-docs',
    author: '陈工程师',
    date: '2026-07-28',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Software%20engineers%20collaborating%20on%20technical%20documentation%20in%20modern%20open%20office%2C%20multiple%20screens%20showing%20code%20and%20docs%2C%20warm%20lighting%2C%20clean%20tech%20workspace%2C%20editorial%20photography&width=800&height=450&seq=article-tech-04&orientation=landscape',
    tags: ['技术文档', 'API文档', '协同编写', '最佳实践'],
  },
  {
    id: '5',
    title: '会议纪要的协同革命：让每一次会议都有完美记录',
    slug: 'meeting-minutes-revolution',
    excerpt: '会议纪要不是一个人的任务。一起草让会议参与者共同实时记录和补充会议要点，确保不遗漏任何关键决策和行动项。',
    content: `
      <h2>引言</h2>
      <p>会议纪要可能是企业中最常见也最容易被忽视的协作场景。传统的做法是指定一个人记录，其他人在会后补充——这种方式常常导致关键信息遗漏、行动项跟踪不清。一起草的实时协同编辑彻底改变了会议纪要的编写方式。</p>

      <h2>会中共创：人人都是记录者</h2>
      <p>在一起草中创建会议纪要文档后，所有参会者都可以在会议进行时实时添加和补充内容。有人记录决策要点，有人补充数据细节，有人标注行动项——每个人的输入在同一份文档中实时呈现，会议结束时纪要也基本完成，效率提升显著。</p>

      <h2>结构化模板确保完整性</h2>
      <p>一起草提供专业的会议纪要模板，包含会议主题、参会人员、讨论议题、决策结果、行动项等标准模块。基于模板记录，不会遗漏任何重要信息。行动项部分支持分配负责人和截止日期，关联到后续的任务跟踪。</p>

      <h2>会后快速分发与跟进</h2>
      <p>会议结束后，纪要可以一键分享给所有相关人员。一起草支持设置文档访问权限，确保敏感会议内容的安全性。行动项自动生成跟踪列表，责任人收到提醒，确保会议决策能够真正落地执行。</p>

      <h2>总结</h2>
      <p>别再让一个人孤军奋战记会议纪要了。一起草的实时共创模式让会议记录变成团队协作的延伸，让每次会议都有完美的记录。</p>
    `,
    category: '会议纪要',
    categorySlug: 'meeting-minutes',
    author: '赵经理',
    date: '2026-07-22',
    readTime: '4 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20meeting%20room%20with%20diverse%20team%20taking%20collaborative%20notes%20on%20laptops%2C%20warm%20natural%20light%20through%20large%20windows%2C%20professional%20environment%2C%20editorial%20photography%20style&width=800&height=450&seq=article-meeting-05&orientation=landscape',
    tags: ['会议纪要', '实时协作', '效率工具', '团队管理'],
  },
  {
    id: '6',
    title: '学术论文协同撰写：研究团队的效率新范式',
    slug: 'academic-paper-collaboration',
    excerpt: '学术研究往往涉及多位合著者。一起草帮助研究团队高效协同撰写和修改论文，让学术产出更加高效。',
    content: `
      <h2>引言</h2>
      <p>学术论文的撰写通常是多位研究者的协作成果。从文献综述到数据分析，从方法论描述到结论提炼，不同作者负责不同部分，但最终需要形成一篇风格统一、逻辑严密的完整论文。一起草为学术团队提供了理想的协同写作环境。</p>

      <h2>分章节协同撰写</h2>
      <p>学术论文通常包含摘要、引言、方法、结果、讨论等标准章节。一起草支持按章节分工撰写，每位研究者专注于自己的专业领域，同时可以实时查看其他章节的进展和内容。整篇论文的引用格式、术语使用保持一致，避免了后期统一格式的繁琐工作。</p>

      <h2>参考文献协作管理</h2>
      <p>学术写作离不开参考文献管理。一起草内置参考文献管理功能，支持常见引用格式（APA、MLA、Chicago等）。团队成员添加的参考文献自动汇总到文献列表，确保引用的一致性和完整性。</p>

      <h2>导师审阅与批注</h2>
      <p>导师或项目负责人可以使用一起草的审阅模式对论文进行系统审查。逐段的批注意见清晰呈现，学生可以直接在批注中回应并修改。所有修改历史完整保留，投稿前还可以邀请同行进行匿名评审。</p>

      <h2>总结</h2>
      <p>学术协作不再被工具所限。一起草让研究团队将更多精力投入研究本身，而非在格式和版本管理上消耗时间。</p>
    `,
    category: '学术论文',
    categorySlug: 'academic-papers',
    author: '刘教授',
    date: '2026-07-15',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Researchers%20collaborating%20on%20academic%20paper%20in%20university%20library%2C%20warm%20reading%20lamp%20light%2C%20bookshelves%20background%2C%20scholarly%20atmosphere%2C%20editorial%20photography%20with%20soft%20shadows&width=800&height=450&seq=article-academic-06&orientation=landscape',
    tags: ['学术论文', '研究协作', '合著', '文献管理'],
  },
  {
    id: '7',
    title: '企业合同管理数字化转型：从纸质到云端',
    slug: 'contract-digital-transformation',
    excerpt: '越来越多的企业正在将合同管理从纸质流程迁移到云端协同平台。一起草如何助力企业合同管理的数字化转型？',
    content: `
      <h2>引言</h2>
      <p>在数字化转型浪潮中，合同管理是最迫切需要变革的环节之一。纸质合同的签署周期长、存储成本高、追溯困难，而传统的Word+邮件模式同样存在效率瓶颈。一起草提供的云端协同合同管理方案，正在帮助越来越多的企业实现合同管理的数字化升级。</p>

      <h2>云端集中管理，告别散落各地的合同文件</h2>
      <p>一起草提供企业级的云端文档管理能力，所有合同文件集中存储，按项目、部门、类型分类归档。支持全文搜索，三秒定位任意合同。权限控制确保只有授权人员可以访问对应合同，安全合规。</p>

      <h2>从起草到签署的全流程追踪</h2>
      <p>每份合同从创建开始，其起草、修改、审阅、定稿、签署的完整生命周期都被记录下来。谁在什么时间做了什么修改，所有操作一目了然。这不仅提升了管理效率，也满足了企业内控和审计的要求。</p>

      <h2>智能模板与条款库</h2>
      <p>一起草内置丰富的合同模板和标准条款库，支持企业自定义模板。法务团队可以维护经过审核的标准条款，业务人员在起草合同时直接引用，既提高了效率，又降低了法律风险。</p>

      <h2>总结</h2>
      <p>合同管理的数字化转型不是可选项，而是必答题。一起草以云端协同、流程追踪和智能模板三大能力，帮助企业完成这场效率革命。</p>
    `,
    category: '合同起草',
    categorySlug: 'contract-drafting',
    author: '张明华',
    date: '2026-07-10',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20corporate%20legal%20team%20reviewing%20digital%20contracts%20on%20large%20displays%2C%20professional%20office%20environment%2C%20warm%20ambient%20lighting%2C%20clean%20minimal%20design%2C%20editorial%20photography&width=800&height=450&seq=article-contract-07&orientation=landscape',
    tags: ['合同管理', '数字化转型', '企业效率', '云端协作'],
  },
  {
    id: '8',
    title: '设计方案协同评审：让甲方乙方高效沟通',
    slug: 'design-proposal-review',
    excerpt: '设计方案评审常常是甲方和乙方之间最耗时的环节。一起草如何让设计方案的协同评审变得高效透明？',
    content: `
      <h2>引言</h2>
      <p>设计方案评审是创意服务行业的核心流程，但也常常成为项目推进的瓶颈。甲方反复提出修改意见，乙方疲于应对变更，沟通成本居高不下。一起草为设计方案的协同评审提供了全新的解决方案。</p>

      <h2>在线方案呈现与实时批注</h2>
      <p>乙方在一起草中创建方案文档，嵌入设计稿和说明文字。甲方可以在线查看方案，并在设计稿的具体位置添加批注，精确指出需要修改的地方。所有意见集中在一个文档中，不再散落在微信、邮件和电话里。</p>

      <h2>版本管理让修改过程透明化</h2>
      <p>方案通常会经历多轮修改。一起草的版本管理功能完整记录每次修改的内容和修改人。甲方可以查看从初稿到终稿的完整演变过程，乙方可以快速定位到某次修改对应的反馈意见。修改历史透明化有效减少了不必要的返工。</p>

      <h2>定稿与交付一键完成</h2>
      <p>方案评审通过后，一起草支持一键导出为多种格式，方便甲方内部流转和存档。完整的评审记录也可以导出为报告，作为项目交付物的一部分。从提案到交付，全程在一起草中完成。</p>

      <h2>总结</h2>
      <p>设计方案评审不再是一场拉锯战。一起草的在线评审、版本管理和导出交付功能，让甲方和乙方的协作变得前所未有的高效和透明。</p>
    `,
    category: '方案策划',
    categorySlug: 'proposal-planning',
    author: '李婷',
    date: '2026-07-05',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Design%20studio%20team%20reviewing%20creative%20proposals%20on%20large%20screen%2C%20modern%20loft%20office%20with%20natural%20light%2C%20colorful%20design%20moodboards%20on%20wall%2C%20editorial%20photography&width=800&height=450&seq=article-design-08&orientation=landscape',
    tags: ['设计方案', '协同评审', '创意服务', '项目管理'],
  },
];

export const featuredArticles = articles.slice(0, 3);